export interface MatrixSnapshotLevel {
    id: string;
    levelIdentifier: string;
    intervalValue: number;
    targetUM: number;
    description?: string;
    assignments: Record<string, boolean | number>;
}

export interface MatrixSnapshotRow {
    id: string;
    itemName: string;
    itemType?: string;
    category?: string;
    manufacturer?: string;
    partNumber?: string;
    vendorName?: string;
    price?: number | null;
    quantity?: number | null;
    unitOfMeasure?: string;
    sortOrder?: number | null;
}

export interface MatrixSnapshotData {
    levels: MatrixSnapshotLevel[];
    rows: MatrixSnapshotRow[];
}

type SnapshotLevelSource = {
    id: string;
    levelIdentifier: string;
    intervalValue: number;
    targetUM: number;
    description?: string | null;
};

type SnapshotRowSource = {
    id: string;
    itemName: string;
    itemType?: string | null;
    category?: string | null;
    manufacturer?: string | null;
    partNumber?: string | null;
    vendorName?: string | null;
    price?: number | null;
    quantity?: number | null;
    unitOfMeasure?: string | null;
    sortOrder?: number | null;
};

export function buildMatrixSnapshotData(
    levels: SnapshotLevelSource[],
    rows: SnapshotRowSource[],
    assignments: Record<string, Record<string, boolean | number>>,
): MatrixSnapshotData {
    return {
        levels: levels.map((level) => ({
            id: level.id,
            levelIdentifier: level.levelIdentifier,
            intervalValue: level.intervalValue,
            targetUM: level.targetUM,
            description: level.description || undefined,
            assignments: assignments[level.id] || {},
        })),
        rows: rows.map((row) => ({
            id: row.id,
            itemName: row.itemName,
            itemType: row.itemType || undefined,
            category: row.category || undefined,
            manufacturer: row.manufacturer || undefined,
            partNumber: row.partNumber || undefined,
            vendorName: row.vendorName || undefined,
            price: row.price ?? undefined,
            quantity: row.quantity ?? undefined,
            unitOfMeasure: row.unitOfMeasure || undefined,
            sortOrder: row.sortOrder ?? undefined,
        })),
    };
}

export function parseMatrixSnapshotData(value: unknown): MatrixSnapshotData | null {
    const parsed = typeof value === 'string' ? safeJson(value) : value;
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as { levels?: unknown; rows?: unknown };
    if (!Array.isArray(record.levels) || !Array.isArray(record.rows)) return null;
    return record as MatrixSnapshotData;
}

function safeJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

export function levelIdsMissingFromSnapshot(currentLevelIds: string[], snapshot: MatrixSnapshotData): string[] {
    const kept = new Set(snapshot.levels.map((level) => level.id));
    return currentLevelIds.filter((id) => !kept.has(id));
}

export function rowIdsMissingFromSnapshot(currentRowIds: string[], snapshot: MatrixSnapshotData): string[] {
    const kept = new Set(snapshot.rows.map((row) => row.id));
    return currentRowIds.filter((id) => !kept.has(id));
}

/** Point checkmarks at a recreated level or row when the saved id is gone. */
export function rewriteSnapshotAssignments(
    assignments: Record<string, boolean | number>,
    idMap: Record<string, string>,
): Record<string, boolean | number> {
    const next: Record<string, boolean | number> = {};
    for (const [partId, value] of Object.entries(assignments)) {
        next[idMap[partId] || partId] = value;
    }
    return next;
}
